import { ANSWER_FACET, COORD_READOUT_DECIMALS, DEGREE_READOUT_DECIMALS, } from './answer-ref.js';
import { MAX_EQUATION_DECIMALS, MAX_EQUATION_SIG_FIGS, MIN_EQUATION_DECIMALS, MIN_EQUATION_SIG_FIGS, } from './variables.js';
import { cardinalPointFromDegrees } from './compass.js';
import { uncertaintyAsked } from './numerical.js';
/**
 * The digit setting a format carries, or null when it carries none.
 *
 * The two ways to say "fewer digits" round differently ON PURPOSE. Significant figures ROUND
 * AND DO NOT PAD — `3` at three figures is `3`, not `3.00` — which is the behaviour a
 * variable's own `sigFigs` already has, stated in its type as "rounds a resolved number and
 * cannot pad". A second meaning for one phrase inside one product costs more than the
 * symmetry. Decimal places DO pad, because that is what the phrase means.
 */
export function digitsIn(format) {
    const m = /^(sf|dp):(\d{1,2})$/.exec(format ?? '');
    if (!m)
        return null;
    const kind = m[1];
    const n = Number(m[2]);
    /*
     * THE BOUNDS A VARIABLE'S OWN ROUNDING USES — 1 to 15 figures, 0 to 10 places. The same
     * question asked in two places should not have two answers, and these are already the
     * numbers the server refuses anything outside of.
     *
     * It also settles a crash. `toPrecision(0)` does not round to nothing, it throws, and a
     * teacher typing `0` into the figures row would have taken the render down. Out of range
     * reads as no setting at all, which is what reaching past the end of a scale means.
     */
    if (kind === 'sf' && (n < MIN_EQUATION_SIG_FIGS || n > MAX_EQUATION_SIG_FIGS))
        return null;
    if (kind === 'dp' && (n < MIN_EQUATION_DECIMALS || n > MAX_EQUATION_DECIMALS))
        return null;
    return { kind, n };
}
/**
 * A typed count brought inside those bounds, so the field cannot store one they would reject.
 *
 * Mirrors `variables-panel`'s own `clampSigFigs`/`clampDecimals` — the same two lines over the
 * same two constants. Shared where it matters, which is the bounds themselves: a second copy
 * of THOSE is how two surfaces come to disagree about what is allowed.
 */
export function clampDigits(kind, n, maxDecimals) {
    /* `maxDecimals` is the SOURCE's own ceiling — a sensor's readout precision. It only binds
       decimal places: a significant-figure count below the instrument's resolution is still a
       rounding, and one above it cannot invent digits because the resolver already capped the
       value before any of this runs. */
    const dpCeiling = Math.min(MAX_EQUATION_DECIMALS, maxDecimals ?? MAX_EQUATION_DECIMALS);
    return kind === 'sf'
        ? Math.min(MAX_EQUATION_SIG_FIGS, Math.max(MIN_EQUATION_SIG_FIGS, Math.round(n)))
        : Math.min(dpCeiling, Math.max(MIN_EQUATION_DECIMALS, Math.round(n)));
}
/** The format token for a typed digit setting, which is what the chip stores. */
export const digitFormat = (kind, n) => `${kind}:${n}`;
/**
 * WHETHER THIS SOURCE'S ANSWER IS A NUMBER WITH DIGITS TO SPEND.
 *
 * Separate from `formatsFor`, which answers "what is on the menu": digits are typed rather
 * than listed, so they are not on it. Not a unit, which is a word — `m/s` has no digits. Not
 * the angle blocks, whose readings `fmtDegrees` already caps at what the instrument resolves,
 * so a second control over the same digits would be two answers to one question.
 */
export function acceptsDigits(blockType, facet) {
    /*
     * EVERY NUMERIC FACET, not just the bare value — which is what this used to say, and the
     * restriction had no reason behind it in the code or its comments.
     *
     * Reported from real content: a Numerical quoted with its `full` facet printed
     * `20.8347834728347 m/s^2`, and the only way to control it was to abandon the full chip and
     * write the number and the unit as two chips side by side. A composite answer is still an
     * answer about one number.
     *
     * NOT A SCALE. Its value is an ordinal — a whole number from 1 to 15, a POSITION on an axis
     * rather than a measurement — so there is no count of figures or places that changes how it
     * reads, and once a teacher can quote "4 — Agree" the number was never the thing wanting
     * rounding. NOT FITB either: a blank holds whatever the student typed, which is text that
     * may or may not be a number.
     */
    switch (blockType) {
        case 'numerical':
            return facet === ANSWER_FACET.value
                || facet === ANSWER_FACET.uncertainty
                || facet === ANSWER_FACET.combined;
        case 'slider':
            return facet === ANSWER_FACET.value
                || facet === ANSWER_FACET.low
                || facet === ANSWER_FACET.high
                || facet === ANSWER_FACET.combined;
        /*
         * A SENSOR CAN BE SHOWN SHORTER, NEVER LONGER — see `maxDigitsFor`.
         *
         * Their readouts are already capped where the resolver builds them, because no phone
         * magnetometer resolves better than about a degree and the extra digits misrepresent the
         * instrument. A teacher rounding further is making a presentation choice; a teacher
         * asking for more would be inventing precision the device never had.
         */
        case 'tilt':
        case 'compass':
        case 'rotation':
            return facet === ANSWER_FACET.value;
        case 'gps':
            return facet === ANSWER_FACET.lat
                || facet === ANSWER_FACET.lng
                || facet === ANSWER_FACET.combined;
        default:
            return false;
    }
}
/**
 * THE MOST DECIMAL PLACES THIS SOURCE CAN HONESTLY SHOW, or null for no ceiling of its own.
 *
 * An instrument's readout is capped at the resolver, so a chip offering more places would
 * print zeros the device never measured — precision invented by a formatting control. The
 * numbers are the readout constants themselves, so the ceiling cannot drift from the thing
 * it is a ceiling on.
 */
export function maxDigitsFor(blockType) {
    if (blockType === 'tilt' || blockType === 'compass' || blockType === 'rotation') {
        return DEGREE_READOUT_DECIMALS;
    }
    return blockType === 'gps' ? COORD_READOUT_DECIMALS : null;
}
/**
 * WHETHER THE UNCERTAINTY TAKES A DIGIT COUNT OF ITS OWN.
 *
 * Only the `full` facet of a Numerical that asked for one: that chip prints two numbers, and
 * one control over both cannot say "9.8 ± 0.3" — it rounds the pair to the same places and
 * prints "9.80 ± 0.30" or "9.8 ± 0.3" but never the convention a physicist writes.
 *
 * The owner chose two independent rows over a convention-driven default, so nothing here
 * derives one from the other: a teacher can author a pair this app would not have chosen.
 */
export function acceptsUncertaintyDigits(blockType, facet, 
/** The SOURCE block's own data — a row for a box the question never shows is a dead row. */
data) {
    if (blockType !== 'numerical' || facet !== ANSWER_FACET.combined)
        return false;
    /* The same gate the facet and the evaluator read — see `uncertaintyAsked`. Without it the
       chip offered a digit count for a number that is never part of the answer, reported as
       "error decimals should not be shown if there was no uncertainty in the original number". */
    return uncertaintyAsked(data);
}
/** Re-spell one numeric string; anything that is not a number is returned untouched. */
function withDigits(text, spec) {
    const v = Number(text);
    if (text.trim() === '' || !Number.isFinite(v))
        return text;
    return spec.kind === 'sf' ? String(parseFloat(v.toPrecision(spec.n))) : v.toFixed(spec.n);
}
/** The formats that change nothing: each domain's own way of saying "as the block recorded it". */
const IDENTITY = new Set(['asWritten', 'decimal', 'degrees']);
/**
 * The formats worth offering for one reference, or none.
 *
 * Keyed on the SOURCE BLOCK and the facet, because that is what decides whether the quoted
 * thing is words, a bearing or a coordinate. A caller showing these needs no table of its own.
 */
export function formatsFor(blockType, facet) {
    // The Compass block's own three, in its own order and under its own names.
    if (blockType === 'compass' || blockType === 'rotation') {
        return ['degrees', 'cardinalDegree', 'cardinalOnly'];
    }
    if (blockType === 'gps') {
        return facet === ANSWER_FACET.lat || facet === ANSWER_FACET.lng || facet === ANSWER_FACET.combined
            ? ['decimal', 'cardinal']
            : [];
    }
    /*
     * WORDS, which is every block whose answer is text the student chose or typed. A number has
     * no case to change, so Numerical, Slider, Scale and Tilt get nothing — and nor does a
     * Numerical's `unit`, where "KG" is not a unit anyone writes.
     */
    if (blockType === 'mcq' || blockType === 'dropdown' || blockType === 'keyword' || blockType === 'fitb') {
        return ['asWritten', 'lower', 'upper'];
    }
    /*
     * A NUMBER OFFERS NOTHING ON THE MENU, and that is not the same as offering nothing: its
     * digits are TYPED, in their own two rows, so they are not a list to pick from. See
     * `acceptsDigits`, which is the question a caller drawing those rows asks.
     */
    return [];
}
/** What a chip with no format of its own reads as: the first its source offers. */
export function defaultFormatFor(blockType, facet) {
    return formatsFor(blockType, facet)[0] ?? 'asWritten';
}
/**
 * Case applied to the text of an answer, leaving any markup in it alone.
 *
 * An option's text is the teacher's rich text, so it can carry emphasis, a colour, a
 * subscript. Lowercasing the string would lowercase its tag names and attributes with it, so
 * only the text between tags moves.
 */
function recase(html, to) {
    return html.replace(/>([^<]+)</g, (_m, text) => `>${to === 'lower' ? text.toLowerCase() : text.toUpperCase()}<`)
        // The common case is a bare string with no tags at all, which the pattern above cannot
        // see because it needs a `>` to its left.
        .replace(/^([^<>]+)/, text => (to === 'lower' ? text.toLowerCase() : text.toUpperCase()))
        .replace(/([^<>]+)$/, text => (to === 'lower' ? text.toLowerCase() : text.toUpperCase()));
}
/** A signed decimal degree as a magnitude and a hemisphere — `-0.12` lng becomes `0.12° W`. */
function hemisphere(raw, axis) {
    const value = Number(raw);
    if (!Number.isFinite(value))
        return raw;
    const letter = axis === 'lat' ? (value < 0 ? 'S' : 'N') : (value < 0 ? 'W' : 'E');
    // The sign is carried by the letter now, so leaving it on would say it twice.
    return `${Math.abs(value)}° ${letter}`;
}
/**
 * A bearing as a compass point, through the Compass block's own naming.
 *
 * Not a table of its own: this app says N, NE, E and so on to eight points, and a reference
 * inventing sixteen would say "NNE" where every other surface says "NE" about the same
 * reading.
 */
function cardinalOf(raw) {
    const value = Number(raw);
    return Number.isFinite(value) ? cardinalPointFromDegrees(value) : null;
}
/**
 * The answer as the chip asks for it.
 *
 * Returns the answer UNCHANGED for a format that does not apply to it, rather than refusing:
 * a reference re-anchored from an MCQ to a Numerical keeps whatever format it had, and a
 * sentence quietly reading the plain number is better than one that reads nothing.
 */
export function applyAnswerFormat(resolved, format, facet, 
/** The uncertainty's own digits, where the chip offers a second row — see
 *  `acceptsUncertaintyDigits`. Omitted, the value's spec covers both halves. */
uncertaintyFormat) {
    const digits = digitsIn(format);
    const uncertaintyDigits = digitsIn(uncertaintyFormat);
    /* AFTER the two specs are read, not before: "as written" is an identity for the VALUE and
       says nothing about the uncertainty, so returning here first made the second row inert
       for the commonest half-setting of the two — round the error, leave the value alone. */
    if (IDENTITY.has(format) && uncertaintyDigits === null)
        return resolved;
    /* EITHER ROW, not just the first: a teacher may round the uncertainty and leave the value
       as written, which is the commonest half-setting of the two. */
    if (digits || uncertaintyDigits) {
        /** Unchanged where that half has no spec of its own. */
        const spell = (text, spec) => (spec === null ? text : withDigits(text, spec));
        /*
         * The number only. A `measure` is a value and a unit, and `m/s` has no digits to round;
         * a list or a pair is not one quantity, so there is nothing a single choice could mean.
         */
        switch (resolved.kind) {
            case 'scalar': return { kind: 'scalar', text: spell(resolved.text, digits) };
            case 'measure': return { ...resolved, value: spell(resolved.value, digits) };
            /* BOTH numbers, because they are one answer and a format that rounded only half of it would
               produce exactly the disagreement the block grades for. */
            /*
             * TWO ROWS, because one cannot write the convention. A single spec rounds both halves
             * the same way and prints "9.80 ± 0.30" or "9.8 ± 0.3", never "9.81 ± 0.3". The second
             * spec falls back to the first, so a teacher who sets only one still gets the old
             * behaviour and a chip written before this existed reads exactly as it did.
             */
            case 'uncertain': return {
                ...resolved,
                value: spell(resolved.value, digits),
                uncertainty: spell(resolved.uncertainty, uncertaintyDigits ?? digits),
            };
            /* A RANGE is one quantity at two points of the same scale, so one spec covers both
               ends — a slider whose low reads 12.5 and whose high reads 80 to different places
               would look like two different measurements. */
            case 'range': return {
                ...resolved,
                low: spell(resolved.low, digits),
                high: spell(resolved.high, digits),
            };
            /* A COORDINATE PAIR, for the same reason: latitude and longitude are one fix. */
            case 'pair': return {
                ...resolved,
                a: spell(resolved.a, digits),
                b: spell(resolved.b, digits),
            };
            default: return resolved;
        }
    }
    if (format === 'lower' || format === 'upper') {
        switch (resolved.kind) {
            case 'scalar': return { kind: 'scalar', text: recase(resolved.text, format) };
            case 'list': return { kind: 'list', items: resolved.items.map(i => recase(i, format)) };
            default: return resolved;
        }
    }
    if (format === 'cardinalOnly' || format === 'cardinalDegree') {
        if (resolved.kind !== 'scalar')
            return resolved;
        const point = cardinalOf(resolved.text);
        if (point === null)
            return resolved;
        return {
            kind: 'scalar',
            // `cardinalDegree` keeps the number and names it, which is the reading a teacher wants
            // when the bearing itself matters and the direction is what makes it legible.
            text: format === 'cardinalOnly' ? point : `${resolved.text}\u00b0 ${point}`,
        };
    }
    // `cardinal` on a coordinate. Which axis a scalar is on comes from the facet; a pair is
    // always latitude then longitude.
    if (resolved.kind === 'pair') {
        return { kind: 'pair', a: hemisphere(resolved.a, 'lat'), b: hemisphere(resolved.b, 'lng') };
    }
    if (resolved.kind === 'scalar' && (facet === ANSWER_FACET.lat || facet === ANSWER_FACET.lng)) {
        return { kind: 'scalar', text: hemisphere(resolved.text, facet === ANSWER_FACET.lat ? 'lat' : 'lng') };
    }
    return resolved;
}
//# sourceMappingURL=answer-format.js.map